import { useEffect, useState } from 'react';
import { Dropdown, Spinner } from 'react-bootstrap';

import Sentry from 'src/services/sentry';

import { isSupportedCameraLocaleCode } from 'src/models/Camera/Settings/Language/CameraLocale.model';
import { Voice } from 'src/models/Camera/Settings/Language/GetLanguages.model';
import { SetLanguageType } from 'src/models/Camera/Settings/Language/SetLanguage.model';

import { useGetCameraLanguages } from 'src/hooks/camera/useGetCameraLanguages';
import { useGetCameraSettings } from 'src/hooks/camera/useGetCameraSettings';
import { useSetCameraLanguage } from 'src/hooks/camera/useSetCameraLanguage';
import useUiTranslation from 'src/hooks/useUiTranslation';

import { captureError } from 'src/utils/errorReporting';
const CameraLanguageDropdown = () => {
  const { tPages } = useUiTranslation();

  const { languages, getCameraLanguages } = useGetCameraLanguages();

  const {
    cameraSettings,
    getCameraSettings,
    setCameraSettings,
    isGettingCameraSettings,
  } = useGetCameraSettings();
  const { setCameraLanguage } = useSetCameraLanguage();

  useEffect(() => {
    getCameraLanguages();
    getCameraSettings();
  }, [getCameraLanguages, getCameraSettings]);

  const [isUpdatingLanguage, setIsUpdatingLanguage] = useState(false);

  const handleSetCameraLanguage = async (lang: Voice) => {
    try {
      if (!cameraSettings) {
        Sentry.captureMessage('Camera settings not found');
        return;
      }

      setIsUpdatingLanguage(true);
      await setCameraLanguage({
        lang: lang.value,
        type: SetLanguageType.Voice,
      });

      setCameraSettings({
        ...cameraSettings,
        obj: {
          ...cameraSettings?.obj,
          settings: {
            ...cameraSettings?.obj.settings,
            voice: lang.value,
          },
        },
      });
    } catch (error) {
      captureError(error, 'Failed to set camera language');
      // TODO: Uncomment this after we receive the error message from the translation company
      // Api.alertBox({
      //   title: tCommon('bootbox.title.Error'),
      //   message: tCommon('message.error.failedToSetCameraLanguage'),
      //   locale: language,
      //   type: 'error',
      // });
    } finally {
      setIsUpdatingLanguage(false);
    }
  };

  const supportedVoices = languages?.obj.voice.filter((lang: Voice) =>
    isSupportedCameraLocaleCode(lang.value),
  );

  return (
    <Dropdown>
      <Dropdown.Toggle variant="primary-100">
        {isUpdatingLanguage || isGettingCameraSettings ? (
          <Spinner size="sm" className="me-2" />
        ) : (
          <>
            <strong>{tPages('newExam.cameraLanguage')}</strong>{' '}
            {(() => {
              const currentVoice = cameraSettings?.obj.settings.voice;

              const foundLangLocaleCode = supportedVoices?.find(
                (lang: Voice) => lang.value === currentVoice,
              )?.value;

              return (
                foundLangLocaleCode &&
                tPages(
                  `newExam.cameraLanguageDropdownItems.${foundLangLocaleCode}`,
                )
              );
            })()}
          </>
        )}
      </Dropdown.Toggle>

      <Dropdown.Menu>
        {supportedVoices?.map((lang: Voice) => (
          <Dropdown.Item
            key={lang.label}
            active={lang.value === cameraSettings?.obj.settings.voice}
            onClick={() => handleSetCameraLanguage(lang)}
          >
            {tPages(`newExam.cameraLanguageDropdownItems.${lang.value}`)}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default CameraLanguageDropdown;
